import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { useConsent } from "@/context/ConsentContext";

const EASE = [0.16, 1, 0.3, 1];

const Toggle = ({ checked, onChange, disabled, id }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    data-testid={id}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 transition-colors duration-300 ${
      checked ? "bg-[#1D4ED8]" : "bg-slate-500"
    } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 bg-white transition-transform duration-300 ${
        checked ? "translate-x-[22px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

export default function CookieBanner() {
  const { consent, decided, acceptAll, rejectNonEssential, savePreferences, managerOpen, setManagerOpen } =
    useConsent();
  const [prefs, setPrefs] = useState({ preferences: false, analytics: false });

  const showBanner = !decided && !managerOpen;

  const openManager = () => {
    setPrefs({
      preferences: consent?.preferences || false,
      analytics: consent?.analytics || false,
    });
    setManagerOpen(true);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            key="cookie-banner"
            data-testid="cookie-banner"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="fixed bottom-0 left-0 right-0 z-[60] md:bottom-6 md:left-6 md:right-auto md:max-w-md"
          >
            <div className="relative bg-[#0A1128] text-white p-6 md:p-7 shadow-2xl">
              <div className="noise" />
              <p className="relative font-display text-lg uppercase tracking-wide">Cookies on this site</p>
              <p className="relative mt-3 text-sm leading-relaxed text-slate-300">
                We use only strictly necessary cookies by default. With your consent we may use preference cookies to
                improve your experience. See our{" "}
                <Link to="/cookie-policy" className="underline underline-offset-2 hover:text-white">
                  Cookie Policy
                </Link>
                .
              </p>
              <div className="relative mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  data-testid="cookie-accept-all"
                  onClick={acceptAll}
                  className="bg-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-[#0A1128] transition-colors hover:bg-slate-200"
                >
                  Accept all
                </button>
                <button
                  data-testid="cookie-reject"
                  onClick={rejectNonEssential}
                  className="border border-white/40 px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-white/10"
                >
                  Reject non-essential
                </button>
                <button
                  data-testid="cookie-manage"
                  onClick={openManager}
                  className="px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-slate-300 underline underline-offset-4 transition-colors hover:text-white"
                >
                  Manage preferences
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {managerOpen && (
          <motion.div
            key="cookie-manager"
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm md:items-center md:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => decided && setManagerOpen(false)}
          >
            <motion.div
              data-testid="cookie-manager-panel"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg bg-white p-8 shadow-2xl"
            >
              {decided && (
                <button
                  aria-label="Close preferences"
                  data-testid="cookie-manager-close"
                  onClick={() => setManagerOpen(false)}
                  className="absolute right-5 top-5 text-slate-500 hover:text-[#0A1128]"
                >
                  <X size={22} />
                </button>
              )}
              <p className="font-display text-2xl uppercase tracking-wide text-[#0A1128]">Cookie preferences</p>
              <p className="mt-2 text-sm text-slate-600">
                Non-essential cookies stay switched off until you choose to enable them.
              </p>

              <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
                <div className="flex items-start justify-between gap-4 py-4">
                  <div>
                    <p className="font-semibold text-[#0A1128]">Strictly necessary</p>
                    <p className="text-sm text-slate-600">Required for the site to function. Always on.</p>
                  </div>
                  <Toggle id="toggle-necessary" checked disabled onChange={() => {}} />
                </div>
                <div className="flex items-start justify-between gap-4 py-4">
                  <div>
                    <p className="font-semibold text-[#0A1128]">Preferences</p>
                    <p className="text-sm text-slate-600">Remember choices to improve your experience.</p>
                  </div>
                  <Toggle
                    id="toggle-preferences"
                    checked={prefs.preferences}
                    onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
                  />
                </div>
                <div className="flex items-start justify-between gap-4 py-4">
                  <div>
                    <p className="font-semibold text-[#0A1128]">Analytics</p>
                    <p className="text-sm text-slate-600">Not currently used on this site. Off by default.</p>
                  </div>
                  <Toggle
                    id="toggle-analytics"
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                <button
                  data-testid="cookie-save-prefs"
                  onClick={() => {
                    savePreferences(prefs);
                    setManagerOpen(false);
                  }}
                  className="bg-[#0A1128] px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#0E1836]"
                >
                  Save preferences
                </button>
                <button
                  data-testid="cookie-accept-all-manager"
                  onClick={() => {
                    acceptAll();
                    setManagerOpen(false);
                  }}
                  className="border border-[#0A1128] px-6 py-3 text-sm font-semibold uppercase tracking-wide text-[#0A1128] transition-colors hover:bg-[#0A1128] hover:text-white"
                >
                  Accept all
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
